// Auto tune (a macro step, macros.ts): the selection rules (Normal, Band, Colour, …) all ∩, each starting from letting
// everything through, their bounds searched for the ones that select every point of a require list and none of a deny
// list, or the best average of the two (balanced accuracy). Pure, run in the macro worker a round at a time
// (macroeval.ts run) while the page shows the best selection so far.
//
// Each feature is a number per point that one rule step's select.ts test compares (Normal up/down, Colour's hue, …),
// with bounds; the step may be inverted (it then takes the points outside them, and those without the number). A point
// passes a feature it has none of (NaN: no normal, a grey's hue, no range…) only while that rule is off or inverted.
// The search works on samples of the two lists: per sample, the gain of passing (to the score, given the step's mode
// and the selection before it). A round is one feature's exact line search: of the samples passing every other
// feature, the run of them by its value with the most gain (Kadane; round through red for hue; from 0 for the bounds
// that only have a top), or the run with the least, inverted. It leans to letting points through: off when that does
// as well, a run widened (an inverted one narrowed) over what gains nothing, its bounds near the points left out, and,
// at the end, each rule dropped or its bounds opened where that does as well; bounds fully open are off (no step). A
// sweep that gains nothing shakes a feature or two at random (seeded) and goes on; the best so far is kept. Its
// outcome is the rules as steps (emit), and the selection is those steps run with evalStep, so the steps written out
// (→ steps) select the same points. More passes (tunePasses) tune them again ∩ what the pass before left, and after
// each, erode then dilate or dilate then erode is kept when it does better. At the end the lists have their way: every
// require point is selected and no deny point (the steps written out are the rules alone).
import * as sel from './select.ts';
import type { Combine, Mask } from './select.ts';
import type { Params, Step } from './macros.ts';
import { RANGE_STEP } from '../lod.ts';
import { dataRange } from './datagroups.ts';
import { type Cloud, type Ctx, type Dens, type Mem, type Out, densKey, evalStep, holds, measure } from './macroeval.ts';

/** How a run is going: rounds, the lists' points selected of those that can be, the score (0-1), the goal met, its rules as steps. */
export interface TuneStat {
  iter: number; iters: number;
  req: [number, number]; deny: [number, number];
  score: number; met: boolean; best: Step[];
  /** Points of the lists hidden or not facing (they can't be selected, so they don't count). */
  hidden: number;
  /** The pass under way, of how many. */
  pass?: number; passes?: number;
}
/** A tuner's progress: how it's going and, when the best got better (and it had time), its selection. */
export interface TuneEv { stat: TuneStat; out: Out | null }

type Shape = 'two' | 'upper' | 'circ';
interface Feat {
  key: string; shape: Shape; int: boolean;
  /** The bounds' range: a bound there lets everything that end through. */
  lo0: number; hi0: number;
  /** The value of point i of cloud k (NaN: none), and its place along the bounds (default the value itself). */
  val(c: Cloud, k: number): (i: number) => number;
  b?(v: number): number;
  /** v is within lo..hi, exactly as the step's test has it, not inverted (default lo <= v <= hi). */
  pass?(v: number, lo: number, hi: number): boolean;
  /** The bounds a step can take. */
  clamp?(lo: number, hi: number): [number, number];
  /** Its step for these bounds (inv and mode added). */
  step(lo: number, hi: number): [Step['t'], Params];
}
interface Bound { on: boolean; inv: boolean; lo: number; hi: number }

/** cos of x degrees, as select.ts works it out (normalCone). */
const cosd = (x: number) => Math.cos((x * Math.PI) / 180);
const acosd = (c: number) => (Math.acos(Math.min(1, c)) * 180) / Math.PI;
const axisDot = (N: Float32Array, a: number) => (i: number) => Math.abs(N[3 * i + a]);   // normalCone's d (two-sided)
const clampDeg = (lo: number, hi: number): [number, number] => [lo, Math.min(90, Math.max(1, hi))];

/** The features of the rules p ticks that some cloud has data for. */
function features(clouds: Cloud[], ctx: Ctx, p: Params, dens: Dens | null): Feat[] {
  const F: Feat[] = [];
  if (p.normal) {
    // within hi of up/down; within hi of horizontal (Normal h: not within 90 - hi of up/down); within hi of x, of y
    F.push({ key: 'z', shape: 'upper', int: false, lo0: 0, hi0: 90, val: c => axisDot(c.N, 2), b: acosd, pass: (v, _, hi) => v >= cosd(hi), clamp: clampDeg, step: (_, hi) => ['normal', { axis: 'z', deg: hi }] });
    F.push({ key: 'h', shape: 'upper', int: false, lo0: 0, hi0: 90, val: c => axisDot(c.N, 2), b: v => 90 - acosd(v), pass: (v, _, hi) => !(v >= cosd(90 - hi)), clamp: clampDeg, step: (_, hi) => ['normal', { axis: 'h', deg: hi }] });
    for (const [key, a] of [['x', 0], ['y', 1]] as const)
      F.push({ key, shape: 'upper', int: false, lo0: 0, hi0: 90, val: c => axisDot(c.N, a), b: acosd, pass: (v, _, hi) => v >= cosd(hi), clamp: clampDeg, step: (_, hi) => ['normal', { axis: key, deg: hi }] });
  }
  if (p.band) F.push({ key: 'band', shape: 'two', int: false, lo0: -1000, hi0: 1000, val: c => i => c.P[3 * i + 2] - ctx.floorZ, step: (lo, hi) => ['band', { plane: 'floor', lo, hi }] });
  if (p.distance && clouds.some(c => c.range))
    F.push({ key: 'distance', shape: 'two', int: true, lo0: 1, hi0: 255, val: c => i => (c.range?.[i] ? c.range[i] : NaN), step: (lo, hi) => ['distance', { lo: lo * RANGE_STEP, hi: hi * RANGE_STEP }] });
  if (p.edges && clouds.some(c => c.edge?.some(e => e & 1))) {
    const known = (c: Cloud, f: (e: number) => number) => (i: number) => (c.edge && c.edge[i] & 1 ? f(c.edge[i]) : NaN);
    F.push({ key: 'edges', shape: 'two', int: true, lo0: 0, hi0: 31, val: c => known(c, e => e >> 3), step: (lo, hi) => ['edges', { side: 'abs', lo: lo / 31, hi: hi / 31, canny: 'any' }] });
    F.push({ key: 'canny', shape: 'two', int: true, lo0: 0, hi0: 1, val: c => known(c, e => (e & 2 ? 1 : 0)), step: lo => ['edges', { side: 'abs', lo: 0, hi: 1, canny: lo >= 1 ? 'on' : 'off' }] });
  }
  if (p.colour) {
    const hsv = (c: Cloud, j: 0 | 1 | 2) => (i: number) => sel.hsv(c.rgb[3 * i], c.rgb[3 * i + 1], c.rgb[3 * i + 2])[j];
    const all = { h0: 0, h1: 360, s0: 0, s1: 1, v0: 0, v1: 1 };
    F.push({
      key: 'hue', shape: 'circ', int: false, lo0: 0, hi0: 360, val: c => hsv(c, 0),
      pass: (v, lo, hi) => {   // sel.colour's hue test
        if (sel.allHues({ ...all, h0: lo, h1: hi })) return true;
        const h0 = ((lo % 360) + 360) % 360, h1 = ((hi % 360) + 360) % 360;
        return h0 > h1 ? v >= h0 || v <= h1 : v >= h0 && v <= h1;
      },
      step: (lo, hi) => ['colour', { ...all, h0: lo, h1: hi }],
    });
    F.push({ key: 'sat', shape: 'two', int: false, lo0: 0, hi0: 1, val: c => hsv(c, 1), step: (lo, hi) => ['colour', { ...all, s0: lo, s1: hi }] });
    F.push({ key: 'value', shape: 'two', int: false, lo0: 0, hi0: 1, val: c => hsv(c, 2), step: (lo, hi) => ['colour', { ...all, v0: lo, v1: hi }] });
  }
  const dg = p.data ? ctx.data?.[String(p.data)] : undefined, dr = dg ? dataRange(dg.v) : null;
  if (dg && dr)   // a data group's values (Data band): bounds from its least to its most
    F.push({ key: 'data', shape: 'two', int: false, lo0: dr.min, hi0: dr.max, val: (_, k) => i => { const x = dg.v[k]?.[i]; return x === undefined ? NaN : x; }, step: (lo, hi) => ['dataBand', { name: String(p.data), lo, hi, nulls: false }] });
  if (p.density && dens)
    F.push({ key: 'density', shape: 'two', int: true, lo0: 1, hi0: 65535, val: (_, k) => i => (dens.d[k]?.[i] ? dens.d[k]![i] : NaN), step: (lo, hi) => ['density', { r: String(p.r), lo, hi }] });
  if (p.view) {
    const { e, f, ortho } = ctx.cam, fl = Math.hypot(f[0], f[1], f[2]) || 1;
    F.push({
      key: 'view', shape: 'two', int: false, lo0: 0, hi0: 180,
      val: c => i => {   // sel.viewCone's angle
        let vx = -f[0] / fl, vy = -f[1] / fl, vz = -f[2] / fl;
        if (!ortho) {
          vx = e[0] - c.P[3 * i]; vy = e[1] - c.P[3 * i + 1]; vz = e[2] - c.P[3 * i + 2];
          const l = Math.hypot(vx, vy, vz) || 1; vx /= l; vy /= l; vz /= l;
        }
        const nx = c.N[3 * i], ny = c.N[3 * i + 1], nz = c.N[3 * i + 2];
        const d = (nx * vx + ny * vy + nz * vz) / Math.hypot(nx, ny, nz);
        return d === d ? (Math.acos(Math.max(-1, Math.min(1, d))) * 180) / Math.PI : NaN;
      },
      pass: (v, lo, hi) => Math.abs(v - (lo + hi) / 2) <= (hi - lo) / 2,
      step: (lo, hi) => ['view', { at: (lo + hi) / 2, spread: (hi - lo) / 2 }],
    });
  }
  if (p.frames && clouds.some(c => c.frame?.some(x => x)))
    F.push({ key: 'frames', shape: 'two', int: true, lo0: 1, hi0: 1e9, val: c => i => (c.frame?.[i] ? c.frame[i] : NaN), step: (lo, hi) => ['frames', { a: lo, b: hi }] });
  if (p.few && clouds.some(c => c.frame?.some(x => x)))
    F.push({
      key: 'few', shape: 'upper', int: true, lo0: 0, hi0: 1e9,
      val: (c, k) => i => {
        const F = c.frame;
        if (!F?.[i]) return NaN;
        return ctx.at[k].ties[c.si ? c.si[i] : 0]?.get(F[i]) ?? 0;
      },
      step: (_, hi) => ['few', { n: hi + 1 }],
    });
  return F;
}

/** Bounds that let every value through (off, unless inverted: then they select only the points without one). */
const open = (f: Feat, b: Bound) => (f.shape === 'circ' ? sel.allHues({ h0: b.lo, h1: b.hi, s0: 0, s1: 1, v0: 0, v1: 1 }) : (f.shape === 'upper' || b.lo <= f.lo0) && b.hi >= f.hi0);

/** The rules' bounds as steps, each to ∩ (the first one takes the tuner's mode when written out); off ones left out. */
function emit(F: Feat[], B: Bound[]): Step[] {
  return F.flatMap((f, j) => {
    const b = B[j];
    if (!b.on) return [];
    const [t, p] = f.step(b.lo, b.hi);
    return [{ t, mode: 'inter' as const, p: { ...p, inv: b.inv } }];
  });
}

/**
 * The selection the steps make (each ∩, from every point that may be selected; stopping once nothing is left, as a
 * run does), combined into cur with mode.
 */
export function applySteps(clouds: Cloud[], cur: Mask[], ctx: Ctx, steps: Step[], mode: Combine, mem: Mem, reach: Mask[]): Mask[] {
  let m: Mask[] = reach.map(r => r.slice());
  for (const s of steps) {
    if (!holds(m)) break;
    const o = evalStep(clouds, m, ctx, { ...s, mode: 'inter' }, mem);
    if (!o.ok) throw new Error(o.say ?? `a ${s.t} step couldn't`);
    m = o.sel;
  }
  return clouds.map((_, k) => sel.combine(mode, cur[k], m[k]));
}

/** The points that may be selected: shown (and facing, with culling). */
export function reachOf(clouds: Cloud[], ctx: Ctx): Mask[] {
  return clouds.map((c, k) => {
    const m = ctx.at[k].live.slice();
    if (ctx.cull !== null) { const fc = sel.facing(c.P, c.N, ctx.cam.e, ctx.cam.f, ctx.cam.ortho, ctx.cull); for (let i = 0; i < m.length; i++) m[i] &= fc[i]; }
    return m;
  });
}

/** How a selection does on the lists (shown clouds, points that may be selected): selected of each, the balanced accuracy. */
function listScore(clouds: Cloud[], ctx: Ctx, reach: Mask[], req: (Mask | null)[], deny: (Mask | null)[], m: Mask[]) {
  let rs = 0, ds = 0, nR = 0, nD = 0;
  clouds.forEach((_, k) => {
    if (ctx.at[k].off) return;
    const R = req[k], D = deny[k], q = reach[k], x = m[k];
    for (let i = 0; i < x.length; i++) if (q[i]) {
      if (R?.[i]) { nR++; rs += x[i]; }
      if (D?.[i]) { nD++; ds += x[i]; }
    }
  });
  const score = (nR ? (nD ? 0.5 : 1) * rs / nR : 0) + (nD ? (nR ? 0.5 : 1) * (1 - ds / nD) : 0);
  return { rs, ds, nR, nD, score, met: rs === nR && ds === 0 };
}

/** A seeded random number in 0..1 (mulberry32). */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

const EPS = 1e-12;
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
/** The most samples of each list the search uses (every k-th of them past this). */
export const MAX_SAMPLES = 150000;
/** Where a bound goes between the last value it lets through and the first it leaves out: this far from that one. */
const NEAR = 0.1;

/**
 * Tune step s (an Auto tune) on the selection cur, from its lists req and deny (per cloud, null: none there):
 * yields how it's going (at most every `every` ms, with the selection when the best got better and there is time for
 * it), and returns the step's outcome (its selection, the best as stat). Stops at the goal, after s.p.iters rounds,
 * or when mem.finish is set. Throws when the lists are empty. cap: the most samples of each list (tests).
 */
export function* tune(clouds: Cloud[], cur: Mask[], ctx: Ctx, s: Step, req: (Mask | null)[], deny: (Mask | null)[], mem: Mem, every = 100, seed = 1, cap = MAX_SAMPLES): Generator<TuneEv, Out & { tune: TuneStat }, void> {
  const p = s.p, mode = s.mode ?? 'replace', at = ctx.at, iters = Math.max(1, Math.round(+p.iters || 1));
  const reach = reachOf(clouds, ctx);
  let nR = 0, nD = 0, hidden = 0;
  clouds.forEach((_, k) => {
    if (at[k].off) return;
    const R = req[k], D = deny[k], q = reach[k];
    for (let i = 0; i < q.length; i++) {
      const r = R ? R[i] : 0, d = D ? D[i] : 0;
      if (!r && !d) continue;
      if (q[i]) { nR += r; nD += d; } else hidden++;
    }
  });
  if (!nR && !nD) throw new Error(hidden ? 'its require and deny points are all hidden' : 'no require or deny points in these scans: select some, then its + buttons');

  let dens: Dens | null = null;
  if (p.density) {
    const key = densKey(ctx, +p.r);
    dens = ctx.dens?.key === key ? ctx.dens : mem.dens?.key === key ? mem.dens : measure(clouds, at, +p.r, key);
    if (dens) mem.dens = dens;
  }
  const F = features(clouds, ctx, p, dens), K = F.length;

  // samples: every k-th point of each list (at most MAX_SAMPLES of each), weighted so each list counts half; the
  // points the selection still gets wrong are added later (at a point's own weight), so it can get those right too
  const sR = Math.max(1, Math.ceil(nR / cap)), sD = Math.max(1, Math.ceil(nD / cap));
  const wR = nR ? (nD ? 0.5 : 1) / nR : 0, wD = nD ? (nR ? 0.5 : 1) / nD : 0;
  const cl: number[] = [], ix: number[] = [], gain: number[] = [], have = new Set<number>();
  let base = 0, kR = 0, kD = 0;
  /** Point i of cloud k as a sample of r, d (each worth that much); false: the mode decides it whatever the rules say. */
  const add = (k: number, i: number, r: number, d: number) => {
    const c = cur[k][i], out = (on: number) => (mode === 'replace' ? on : mode === 'add' ? c | on : mode === 'sub' ? c & (on ^ 1) : c & on);
    const worth = (on: number) => (out(on) ? r : d);
    const w0 = worth(0), w1 = worth(1);
    base += w0;
    have.add(k * 4294967296 + i);
    if (w1 === w0) return false;
    cl.push(k); ix.push(i); gain.push(w1 - w0);
    return true;
  };
  clouds.forEach((_, k) => {
    if (at[k].off) return;
    const R = req[k], D = deny[k], q = reach[k];
    for (let i = 0; i < q.length; i++) {
      if (!q[i]) continue;
      let r = !!(R && R[i]), d = !!(D && D[i]);
      if (r && kR++ % sR) r = false;
      if (d && kD++ % sD) d = false;
      if (r || d) add(k, i, r ? wR * sR : 0, d ? wD * sD : 0);
    }
  });
  let S = 0, g = new Float64Array(0), V: Float64Array[] = [], Bv: Float64Array[] = [];
  let order: { o: Uint32Array; nan: Uint32Array }[] = [], fails = new Uint8Array(0), failK: Uint8Array[] = [], top = 0;
  const offOf = (f: Feat): Bound => ({ on: false, inv: false, lo: f.lo0, hi: f.hi0 });
  const B: Bound[] = F.map(offOf);
  const passes = (j: number, t: number, b: Bound) => {
    if (!b.on) return true;
    const v = V[j][t];
    if (v !== v) return b.inv;
    const f = F[j], x = f.pass ? f.pass(v, b.lo, b.hi) : v >= b.lo && v <= b.hi;
    return b.inv ? !x : x;
  };
  let score = base;
  /** The samples' values, their order along each feature's bounds (NaN ones apart), and fails with the bounds as they are. */
  const build = () => {
    S = gain.length; g = Float64Array.from(gain);
    V = F.map(() => new Float64Array(S)); Bv = F.map(() => new Float64Array(S));
    F.forEach((f, j) => {
      const vs = clouds.map((c, k) => f.val(c, k));
      for (let t = 0; t < S; t++) { const v = vs[cl[t]](ix[t]); V[j][t] = v; Bv[j][t] = v === v && f.b ? f.b(v) : v; }
    });
    order = Bv.map(b => {
      const o: number[] = [], nan: number[] = [];
      for (let t = 0; t < S; t++) (b[t] === b[t] ? o : nan).push(t);
      o.sort((x, y) => b[x] - b[y]);
      return { o: Uint32Array.from(o), nan: Uint32Array.from(nan) };
    });
    fails = new Uint8Array(S); failK = F.map(() => new Uint8Array(S));
    F.forEach((_, j) => { for (let t = 0; t < S; t++) if (!passes(j, t, B[j])) { failK[j][t] = 1; fails[t]++; } });
    score = base; top = base;
    for (let t = 0; t < S; t++) { if (!fails[t]) score += g[t]; if (g[t] > 0) top += g[t]; }
  };
  build();
  /** Feature j's bounds set to b: the fails and score after. */
  const set = (j: number, b: Bound) => {
    const f = F[j];
    if (b.on && f.clamp) [b.lo, b.hi] = f.clamp(b.lo, b.hi);
    if (b.on && !b.inv && open(f, b)) b = offOf(f);   // fully open: off (no step)
    B[j] = b;
    const fk = failK[j];
    for (let t = 0; t < S; t++) {
      const x = passes(j, t, b) ? 0 : 1;
      if (x !== fk[t]) { fails[t] += x - fk[t]; fk[t] = x; }
    }
    score = base;
    for (let t = 0; t < S; t++) if (!fails[t]) score += g[t];
  };

  /** Feature j's best bounds with the others as they are, leaning to letting points through. */
  const line = (j: number): Bound => {
    const f = F[j], fk = failK[j], bv = Bv[j], { o, nan } = order[j], off = offOf(f);
    const free = (t: number) => fails[t] - fk[t] === 0;
    let nanSum = 0, tot = 0;
    for (const t of nan) if (free(t)) nanSum += g[t];
    const gv: number[] = [], gs: number[] = [];
    for (const t of o) {
      if (!free(t)) continue;
      const v = bv[t];
      if (gv.length && gv[gv.length - 1] === v) gs[gs.length - 1] += g[t]; else { gv.push(v); gs.push(g[t]); }
      tot += g[t];
    }
    const n = gv.length, all = tot + nanSum;
    if (!n) return off;
    // the run a..b with the most of w: from the start only (upper), anywhere (two), or round the end too (circ)
    const run = (w: (i: number) => number, round: boolean) => {
      let best = -Infinity, a = 0, b = 0, wrap = false;
      if (f.shape === 'upper') {
        let sum = 0;
        for (let i = 0; i < n; i++) { sum += w(i); if (sum > best) { best = sum; b = i; } }
        return { best, a, b, wrap };
      }
      let sum = 0, s0 = 0;
      for (let i = 0; i < n; i++) {
        if (sum <= 0) { sum = 0; s0 = i; }
        sum += w(i);
        if (sum > best) { best = sum; a = s0; b = i; }
      }
      if (round && n > 2) {   // round through the end: all but the run with the least (inside)
        let t = 0, mn = Infinity, ms = 0, me = 0, m = 0, m0 = 1;
        for (let i = 0; i < n; i++) t += w(i);
        for (let i = 1; i < n - 1; i++) {
          if (m >= 0) { m = 0; m0 = i; }
          m += w(i);
          if (m < mn) { mn = m; ms = m0; me = i; }
        }
        if (t - mn > best) { best = t - mn; a = me + 1; b = ms - 1; wrap = true; }
      }
      return { best, a, b, wrap };
    };
    const A = run(i => gs[i], f.shape === 'circ');   // the run that passes
    const E = run(i => -gs[i], false);               // inverted: the run left out (the rest and the NaN ones pass)
    const sA = A.best, sE = all + E.best;
    if (Math.max(sA, sE) <= all + EPS) return off;   // off does as well
    // a bound between u (let through) and x (left out), near x; rounded when that leaves it on the same side of both
    const tidy = (v: number, u: number, x: number) => { const r = Math.round(v * 1e6) / 1e6; return Math.sign(r - u) === Math.sign(v - u) && Math.sign(r - x) === Math.sign(v - x) ? r : v; };
    const between = (u: number, x: number) => (f.int ? (x > u ? x - 1 : x + 1) : tidy(x + NEAR * (u - x), u, x));
    if (sA >= sE - EPS) {   // widened over what gains nothing
      let { a, b } = A;
      if (!A.wrap) {
        if (f.shape !== 'upper') for (let k = a - 1, acc = 0; k >= 0; k--) { acc += gs[k]; if (acc >= -EPS) a = k; }
        for (let k = b + 1, acc = 0; k < n; k++) { acc += gs[k]; if (acc >= -EPS) b = k; }
      }
      const lo = f.shape === 'upper' || (a === 0 && !A.wrap) ? f.lo0 : between(gv[a], gv[a - 1]);
      const hi = b === n - 1 && !A.wrap ? f.hi0 : between(gv[b], gv[b + 1]);
      return { on: true, inv: false, lo, hi };
    }
    // inverted: the run left out narrowed past what loses nothing
    let { a, b } = E;
    if (f.shape !== 'upper') for (let k = a, acc = 0; k < b; k++) { acc -= gs[k]; if (acc <= EPS) a = k + 1; }
    for (let k = b, acc = 0; k > a; k--) { acc -= gs[k]; if (acc <= EPS) b = k - 1; }
    // its bounds near the run left out (the points just outside it pass)
    const near = (x: number, u: number) => (f.int ? x : tidy(x + NEAR * (u - x), u, x));
    const lo = f.shape === 'upper' || a === 0 ? f.lo0 : near(gv[a], gv[a - 1]);
    const hi = b === n - 1 ? f.hi0 : near(gv[b], gv[b + 1]);
    return { on: true, inv: true, lo, hi };
  };

  const rand = rng(seed);
  let bestB = B.map(b => ({ ...b })), bestScore = score, iter = 0, lastYield = now(), lastOut = 0, outCost = 0, dirty = true;
  /** Every sample comes out right. */
  const perfect = () => bestScore >= top - 1e-12;
  /** The balanced accuracy of these counts. */
  const exact = (rs: number, ds: number) => (nR ? (nD ? 0.5 : 1) * rs / nR : 0) + (nD ? (nR ? 0.5 : 1) * (1 - ds / nD) : 0);
  const stepsOf = (bs: Bound[]) => emit(F, bs);
  /** The selection the best makes, the exact counts, and the stat. */
  const full = () => {
    const t0 = now(), steps = stepsOf(bestB), m = applySteps(clouds, cur, ctx, steps, mode, mem, reach);
    let rs = 0, ds = 0;
    clouds.forEach((_, k) => {
      if (at[k].off) return;
      const R = req[k], D = deny[k], q = reach[k], x = m[k];
      for (let i = 0; i < x.length; i++) if (q[i] && x[i]) { if (R?.[i]) rs++; if (D?.[i]) ds++; }
    });
    outCost = now() - t0;
    lastOut = now();
    const stat: TuneStat = { iter, iters, req: [rs, nR], deny: [ds, nD], score: exact(rs, ds), met: rs === nR && ds === 0, best: steps, hidden };
    const out: Out = { ok: true, sel: m, changed: true, rule: { leaf: { t: 'tune', steps }, mode } };
    return { stat, out };
  };
  // the lists' counts are those of the last selection worked out; the rounds and score are now's
  let last = full();
  const quick = (): TuneStat => ({ ...last.stat, iter, score: Math.min(1, bestScore) });
  yield last;   // everything let through, to start from

  for (let more = 0; ; more++) {
    let stale = 0;
    while (K && iter < iters && !perfect() && !mem.finish) {
      for (const j of Array.from({ length: K }, (_, x) => x).sort(() => rand() - 0.5)) {
        if (iter >= iters || perfect() || mem.finish) break;
        iter++;
        const was = { ...B[j] }, before = score;
        set(j, line(j));
        if (score < before - 1e-12) set(j, was);   // its bounds clamped to what a step takes did worse
        if (score > bestScore + 1e-12) { bestScore = score; bestB = B.map(b => ({ ...b })); stale = 0; dirty = true; } else stale++;
        const t = now();
        if (t - lastYield >= every) {
          lastYield = t;
          if (dirty && t - lastOut >= Math.max(250, 3 * outCost)) { dirty = false; last = full(); yield last; }
          else yield { stat: quick(), out: null };
        }
      }
      if (stale >= K && !perfect() && iter < iters) {   // stuck: shake a feature or two, from the best
        B.forEach((_, j) => set(j, { ...bestB[j] }));
        for (let n = 1 + (rand() < 0.5 ? 1 : 0); n > 0; n--) {
          const j = Math.floor(rand() * K), f = F[j], { o } = order[j];
          if (rand() < 0.5 || o.length < 2) { set(j, offOf(f)); continue; }
          const x = Bv[j][o[Math.floor(rand() * o.length)]], y = Bv[j][o[Math.floor(rand() * o.length)]];
          set(j, { on: true, inv: rand() < 0.25, lo: f.shape === 'upper' ? f.lo0 : Math.min(x, y), hi: Math.max(x, y) });
        }
        stale = 0;
      }
    }
    // the best, less any rule it does as well without (fewer bounds, fewer points left out that no list has)
    B.forEach((_, j) => set(j, { ...bestB[j] }));
    // (each tried off with the others' bounds searched again, keeping it off when that does as well)
    for (let j = 0; j < K && !mem.finish; j++) {
      if (!B[j].on) continue;
      set(j, offOf(F[j]));
      for (let x = 0; x < K && score < bestScore - 1e-12; x++) if (x !== j && B[x].on) { const was = { ...B[x] }, before = score; set(x, line(x)); if (score < before - 1e-12) set(x, was); }
      if (score >= bestScore - 1e-12) { bestScore = Math.max(bestScore, score); bestB = B.map(b => ({ ...b })); }
      else B.forEach((_, k) => set(k, { ...bestB[k] }));
    }
    // and each side of a rule's bounds opened fully where that does as well
    for (let j = 0; j < K && !mem.finish; j++) {
      const f = F[j];
      if (!B[j].on || B[j].inv || f.shape === 'circ') continue;
      for (const side of f.shape === 'upper' ? ['hi'] as const : ['lo', 'hi'] as const) {
        if (!B[j].on) break;
        const was = { ...B[j] };
        set(j, { ...was, [side]: side === 'lo' ? f.lo0 : f.hi0 });
        if (score >= bestScore - 1e-12) { bestScore = Math.max(bestScore, score); bestB = B.map(b => ({ ...b })); } else set(j, was);
      }
    }
    last = full();
    if (last.stat.met || mem.finish || iter >= iters || more >= 3 || !K) break;
    // the samples came out right, the rest of the lists not quite: those it gets wrong become samples too
    const miss: [number, number, number, number][] = [];
    clouds.forEach((_, k) => {
      if (at[k].off) return;
      const R = req[k], D = deny[k], q = reach[k], x = last.out.sel[k];
      for (let i = 0; i < x.length; i++) if (q[i] && !have.has(k * 4294967296 + i) && ((R?.[i] && !x[i]) || (D?.[i] && x[i]))) miss.push([k, i, R?.[i] ? wR : 0, D?.[i] ? wD : 0]);
    });
    if (!miss.length) break;
    const nth = Math.ceil(miss.length / 40000);
    for (let n = 0; n < miss.length; n += nth) add(...miss[n]);
    build();
    bestScore = score;   // the best (the bounds as they are) on these samples
    bestB = B.map(b => ({ ...b }));
  }
  const { stat, out } = last;
  return { ...out, tune: stat, say: `Auto tune: ${sayStat(stat)}` };
}

/** "require 1,234 of 1,240, deny 3 of 560 (score 99.4%), after 120 rounds": a stat in a line. */
export function sayStat(t: TuneStat): string {
  const pc = (a: number, b: number) => (b ? `${((100 * a) / b).toFixed(1)}%` : '–');
  const lists = t.req[1] || t.deny[1] ? `require ${t.req[0].toLocaleString()} of ${t.req[1].toLocaleString()} (${pc(t.req[0], t.req[1])}), deny ${t.deny[0].toLocaleString()} of ${t.deny[1].toLocaleString()} (${pc(t.deny[0], t.deny[1])})` : '';
  const pass = t.passes && t.passes > 1 ? ` (pass ${t.pass} of ${t.passes})` : '';
  return `${t.met ? 'goal met: ' : ''}${lists}${lists ? ', ' : ''}score ${(100 * t.score).toFixed(1)}% after ${t.iter} of ${t.iters} rounds${pass}${t.hidden ? `; ${t.hidden.toLocaleString()} of the lists' points hidden` : ''}`;
}

/**
 * Step s (an Auto tune) in its passes: the first from cur with its mode, each after it ∩ what the one before left
 * (stopping at the goal, a Finish, or once nothing is left); after each, with s.p.morph, erode then dilate or dilate
 * then erode, by up to s.p.mr, kept when it does better on the lists. Yields and returns as tune does, its best being
 * every pass's steps (and the erode / dilate kept) in order, its stat how they did; its selection is theirs with every
 * require point put in and every deny point taken out (of the points that may be selected).
 */
export function* tunePasses(clouds: Cloud[], cur: Mask[], ctx: Ctx, s: Step, req: (Mask | null)[], deny: (Mask | null)[], mem: Mem, every = 100, cap = MAX_SAMPLES): Generator<TuneEv, Out & { tune: TuneStat }, void> {
  const P = Math.max(1, Math.round(+s.p.passes || 1)), iters = Math.max(1, Math.round(+s.p.iters || 1)), reach = reachOf(clouds, ctx);
  let selNow = cur, steps: Step[] = [], done = 0, res: (Out & { tune: TuneStat }) | null = null;
  const wrap = (st: TuneStat, pass: number, more: Step[] = []): TuneStat => ({ ...st, iter: done + st.iter, iters: iters * P, best: [...steps, ...more], pass: pass + 1, passes: P });
  for (let pass = 0; pass < P; pass++) {
    if (pass && (!holds(selNow) || mem.finish)) break;
    const g = tune(clouds, selNow, ctx, pass ? { ...s, mode: 'inter' } : s, req, deny, mem, every, 1 + pass, cap);
    let r: Out & { tune: TuneStat };
    for (;;) {
      const it = g.next();
      if (it.done) { r = it.value; break; }
      yield { stat: wrap(it.value.stat, pass, it.value.stat.best), out: it.value.out };
    }
    selNow = r.sel;
    done += r.tune.iter;
    steps = [...steps, ...r.tune.best];
    let stat = wrap({ ...r.tune, iter: 0 }, pass), out: Out = r;
    // erode then dilate (opens: drops thin bits and strays), dilate then erode (closes: fills small holes)
    if (s.p.morph && !stat.met && !mem.finish) {
      let best = listScore(clouds, ctx, reach, req, deny, selNow);
      const R = Math.max(0.005, +s.p.mr || 0.05);
      for (const r0 of [R, R / 2, R / 4].filter(x => x >= 0.005))
        for (const order of [['erode', 'dilate'], ['dilate', 'erode']] as const) {
          if (mem.finish) break;
          const ms: Step[] = order.map(t => ({ t, p: { r: Math.round(r0 * 1e4) / 1e4 } }));
          let m = selNow, ok = true;
          for (const st of ms) { const o = evalStep(clouds, m, ctx, st, mem); if (!o.ok) { ok = false; break; } m = o.sel; }
          if (!ok) continue;
          const sc = listScore(clouds, ctx, reach, req, deny, m);
          if (sc.score > best.score + 1e-12) {
            best = sc; selNow = m; out = { ...out, sel: m };
            stat = { ...stat, best: [...steps, ...ms], req: [sc.rs, sc.nR], deny: [sc.ds, sc.nD], score: sc.score, met: sc.met };
            yield { stat, out: { ...out, sel: m } };
          } else yield { stat, out: null };
        }
      steps = stat.best;
    }
    res = { ...out, sel: selNow, tune: stat };
    if (stat.met) break;
  }
  // then the lists have their way: every require point in, every deny point out (of those that may be selected; the
  // steps written out don't do this, only the rules)
  const stat = res!.tune;
  let added = 0, dropped = 0;
  const fin = selNow.map((m0, k) => {
    if (ctx.at[k].off) return m0;
    const m = m0.slice(), R = req[k], D = deny[k], q = reach[k];
    for (let i = 0; i < m.length; i++) {
      if (!q[i]) continue;
      if (R?.[i] && !m[i]) { m[i] = 1; added++; }
      if (D?.[i] && m[i]) { m[i] = 0; dropped++; }
    }
    return m;
  });
  const forced = added || dropped ? `; then ${[added && `${added.toLocaleString()} require points put in`, dropped && `${dropped.toLocaleString()} deny points taken out`].filter(Boolean).join(', ')}` : '';
  return { ...res!, sel: fin, changed: true, rule: { leaf: { t: 'tune', steps: stat.best }, mode: s.mode ?? 'replace' }, tune: stat, say: `Auto tune: ${sayStat(stat)}${forced}` };
}
